import {useEffect, useState} from "react";
import {Button, DatePicker, Empty, Input, message, Select, Space, Spin} from "antd";
import {
    LeftOutlined,
    NumberOutlined,
    ReloadOutlined,
    RightOutlined,
    VerticalAlignTopOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import ApiService from "../../../core/service/api-service";
import "./AppLogViewer.css";

const LEVELS = {
    Verbose: {code: 'VRB', color: '#8c8c8c'},
    Debug: {code: 'DBG', color: '#8c8c8c'},
    Information: {code: 'INF', color: '#1890ff'},
    Warning: {code: 'WRN', color: '#faad14'},
    Error: {code: 'ERR', color: '#f5222d'},
    Fatal: {code: 'FTL', color: '#a8071a'},
};

// Bilinen mantıksal kanallar için fallback (dinamik liste boşsa). Sunucu app_log_dimensions'tan gerçek listeyi döndürür.
const CHANNELS = ['HL7Process', 'Exception', 'Study', 'Http'];

// Logun kahramanı (aktör) tipleri — sabit küme.
const ACTOR_TYPES = {
    User: {code: 'USR', color: '#1890ff'},
    Client: {code: 'CLI', color: '#13c2c2'},
    ShareToken: {code: 'SHR', color: '#722ed1'},
    Anonymous: {code: 'ANON', color: '#8c8c8c'},
    System: {code: 'SYS', color: '#fa8c16'},
};

function formatTime(ts) {
    const d = new Date(ts);
    const pad = (n, l = 2) => String(n).padStart(l, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function shortSource(source) {
    if (!source) return '';
    const parts = source.split('.');
    return parts[parts.length - 1];
}

function prettyJson(jsonStr) {
    try {
        return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch {
        return jsonStr;
    }
}

export default function AppLogViewer() {
    const {t} = useTranslation();

    const [levels, setLevels] = useState([]);
    const [search, setSearch] = useState('');
    const [sourceContext, setSourceContext] = useState('');
    const [channel, setChannel] = useState(undefined);
    const [requestId, setRequestId] = useState('');
    const [actorType, setActorType] = useState(undefined);
    const [actorName, setActorName] = useState('');
    const [ip, setIp] = useState('');
    const [clientId, setClientId] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [pageSize, setPageSize] = useState(100);

    const [items, setItems] = useState([]);
    const [hasPrev, setHasPrev] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [ascending, setAscending] = useState(false);
    const [pageIndex, setPageIndex] = useState(1);
    const [loading, setLoading] = useState(false);
    const [expandedIds, setExpandedIds] = useState(new Set());

    const [exactCount, setExactCount] = useState(null);
    const [countLoading, setCountLoading] = useState(false);

    const [sourceOptions, setSourceOptions] = useState([]);
    const [channelOptions, setChannelOptions] = useState([]);

    const buildFilter = (overrides = {}) => ({
        levels: levels.length > 0 ? levels : null,
        search: search || null,
        sourceContext: sourceContext || null,
        channel: channel || null,
        requestId: requestId || null,
        actorType: actorType || null,
        actorName: actorName || null,
        ip: ip || null,
        clientId: clientId || null,
        dateFrom: dateRange?.[0]?.toISOString() ?? null,
        dateTo: dateRange?.[1]?.toISOString() ?? null,
        ...overrides,
    });

    const fetchPage = async ({prev, next, reset, filterOverride, ascendingOverride} = {}) => {
        setLoading(true);
        const asc = ascendingOverride ?? ascending;
        const response = await ApiService.Log.GetAppLogPage({
            filter: buildFilter(filterOverride),
            pageSize,
            ascending: asc,
            prev,
            next
        });
        setLoading(false);

        if (!response.success)
            return;

        const data = response.data;

        // Sınıra gelindi: sayfa boş döndüyse mevcut görünümü koru, yönü kapat
        if ((prev || next) && (!data.items || data.items.length === 0)) {
            if (next) setHasNext(false);
            if (prev) setHasPrev(false);
            return;
        }

        setItems(data.items ?? []);
        setHasPrev(data.hasPrev);
        setHasNext(data.hasNext);
        setExpandedIds(new Set());
        setPageIndex(p => reset ? 1 : (next ? p + 1 : Math.max(1, p - 1)));
    };

    const goStart = () => fetchPage({reset: true});

    const goNext = () => {
        const last = items[items.length - 1];
        if (!last) return;
        fetchPage({next: {timestamp: last.timestamp, id: last.id}});
    };

    const goPrev = () => {
        const first = items[0];
        if (!first) return;
        fetchPage({prev: {timestamp: first.timestamp, id: first.id}});
    };

    // Sıralama yönünü değiştir (yeniden eskiye / eskiden yeniye) ve başa dön.
    const changeOrder = (asc) => {
        setAscending(asc);
        fetchPage({reset: true, ascendingOverride: asc});
    };

    const getExactCount = async () => {
        setCountLoading(true);
        const response = await ApiService.Log.GetAppLogCount(buildFilter());
        setCountLoading(false);
        if (response.success) {
            setExactCount(response.data);
            message.success(`${t('exactCount')}: ${response.data.toLocaleString()}`);
        }
    };

    useEffect(() => {
        goStart();
        setExactCount(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);

    // Kaynak (servis) ve kanal dropdown'larını Mongo'daki gerçek değerlerden doldur.
    useEffect(() => {
        (async () => {
            const s = await ApiService.Log.GetAppLogSources();
            if (s.success) setSourceOptions(s.data ?? []);
            const c = await ApiService.Log.GetAppLogChannels();
            if (c.success) setChannelOptions(c.data ?? []);
        })();
    }, []);

    // Satırdaki bir kaynağa/kanala tıklayınca ona göre filtreleyip en yeni sayfaya dön.
    // Değer doğrudan fetchPage'e geçilir; state güncellemesi yalnızca kontrolü senkronlar (stale closure önlenir).
    const filterBySource = (value) => {
        setSourceContext(value || '');
        fetchPage({reset: true, filterOverride: {sourceContext: value || null}});
    };
    const filterByChannel = (value) => {
        setChannel(value || undefined);
        fetchPage({reset: true, filterOverride: {channel: value || null}});
    };
    // Bir isteğin correlation id'sine tıklayınca o isteğin tüm loglarını (girdiden çıktıya akış) göster.
    const filterByRequestId = (value) => {
        setRequestId(value || '');
        fetchPage({reset: true, filterOverride: {requestId: value || null}});
    };
    const filterByActorType = (value) => {
        setActorType(value || undefined);
        fetchPage({reset: true, filterOverride: {actorType: value || null}});
    };
    const filterByActor = (value) => {
        setActorName(value || '');
        fetchPage({reset: true, filterOverride: {actorName: value || null}});
    };
    // Bir IP'ye tıklayınca o IP'nin tüm etkinliğini (aktör tipinden bağımsız) göster.
    const filterByIp = (value) => {
        setIp(value || '');
        fetchPage({reset: true, filterOverride: {ip: value || null}});
    };
    // Bir cihaz kimliğine (clientId) tıklayınca o cihazın tüm etkinliğini göster (aynı IP+kullanıcıdaki tek PC).
    const filterByClientId = (value) => {
        setClientId(value || '');
        fetchPage({reset: true, filterOverride: {clientId: value || null}});
    };

    // Tarih aralığını uygula (state + anında fetch; stale closure'ı override ile önle).
    const applyDateRange = (range) => {
        setDateRange(range);
        fetchPage({reset: true, filterOverride: {
            dateFrom: range?.[0]?.toISOString() ?? null,
            dateTo: range?.[1]?.toISOString() ?? null,
        }});
    };

    // Hızlı seçim: son N takvim günü (bugün dahil). N=1 → sadece bugün.
    const presetLastDays = (n) =>
        applyDateRange([moment().subtract(n - 1, 'days').startOf('day'), moment().endOf('day')]);

    // Dün: sadece dünkü tam gün.
    const presetYesterday = () => {
        const d = moment().subtract(1, 'days');
        applyDateRange([d.clone().startOf('day'), d.clone().endOf('day')]);
    };

    // İleri/geri: mevcut aralığı kendi uzunluğu (gün) kadar kaydır. Aralık yoksa bugüne düş.
    const shiftDateRange = (dir) => {
        if (!dateRange?.[0] || !dateRange?.[1]) {
            presetLastDays(1);
            return;
        }
        const days = Math.max(1, dateRange[1].diff(dateRange[0], 'days') + 1);
        applyDateRange([
            dateRange[0].clone().add(dir * days, 'days'),
            dateRange[1].clone().add(dir * days, 'days'),
        ]);
    };

    const toggleExpanded = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const levelOptions = Object.entries(LEVELS).map(([value, meta]) => ({
        value,
        label: <span style={{color: meta.color, fontWeight: 600}}>{meta.code}</span>,
    }));

    return (
        <>
            <Space wrap style={{marginBottom: 8}}>
                <Select
                    mode="multiple"
                    allowClear
                    style={{minWidth: 140}}
                    placeholder={t('Level')}
                    options={levelOptions}
                    value={levels}
                    onChange={setLevels}
                    maxTagCount={3}
                />
                <Input
                    allowClear
                    style={{width: 220}}
                    placeholder={t('Search in message')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onPressEnter={goStart}
                />
                <Select
                    allowClear
                    showSearch
                    style={{minWidth: 200}}
                    placeholder={t('Source')}
                    value={sourceContext || undefined}
                    onChange={filterBySource}
                    options={sourceOptions.map(s => ({value: s, label: shortSource(s), title: s}))}
                    filterOption={(input, option) =>
                        `${option.value} ${option.label}`.toLowerCase().includes(input.toLowerCase())}
                    notFoundContent={t('No logs found')}
                />
                <Select
                    allowClear
                    showSearch
                    style={{minWidth: 150}}
                    placeholder={t('Channel')}
                    value={channel}
                    onChange={filterByChannel}
                    options={(channelOptions.length ? channelOptions : CHANNELS).map(c => ({value: c, label: c}))}
                />
                <Select
                    allowClear
                    style={{minWidth: 130}}
                    placeholder={t('Actor Type')}
                    value={actorType}
                    onChange={filterByActorType}
                    options={Object.keys(ACTOR_TYPES).map(v => ({value: v, label: v}))}
                />
                <Input
                    allowClear
                    style={{width: 150}}
                    placeholder={t('Actor')}
                    value={actorName}
                    onChange={e => setActorName(e.target.value)}
                    onPressEnter={goStart}
                />
                <Input
                    allowClear
                    style={{width: 140}}
                    placeholder={t('IP')}
                    value={ip}
                    onChange={e => setIp(e.target.value)}
                    onPressEnter={goStart}
                />
                <Input
                    allowClear
                    style={{width: 150}}
                    placeholder={t('Device')}
                    value={clientId}
                    onChange={e => setClientId(e.target.value)}
                    onPressEnter={goStart}
                />
                <Input
                    allowClear
                    style={{width: 170}}
                    placeholder={t('Request Id')}
                    value={requestId}
                    onChange={e => { const v = e.target.value; setRequestId(v); if (!v) filterByRequestId(''); }}
                    onPressEnter={() => filterByRequestId(requestId)}
                />
                <DatePicker.RangePicker
                    showTime
                    value={dateRange}
                    onChange={applyDateRange}
                />
                <Button.Group>
                    <Button icon={<LeftOutlined />} title={t('Previous period')} onClick={() => shiftDateRange(-1)} />
                    <Button onClick={() => presetLastDays(1)}>{t('Today')}</Button>
                    <Button onClick={presetYesterday}>{t('Yesterday')}</Button>
                    <Button onClick={() => presetLastDays(3)}>{t('Last 3 days')}</Button>
                    <Button onClick={() => presetLastDays(7)}>{t('Last 7 days')}</Button>
                    <Button onClick={() => presetLastDays(30)}>{t('Last 30 days')}</Button>
                    <Button icon={<RightOutlined />} title={t('Next period')} onClick={() => shiftDateRange(1)} />
                </Button.Group>
                <Button type="primary" icon={<ReloadOutlined />} onClick={goStart} loading={loading}>
                    {t('Search')}
                </Button>
                <Button icon={<NumberOutlined />} onClick={getExactCount} loading={countLoading}>
                    {t('getExactCount')}
                </Button>
                {exactCount !== null && (
                    <span style={{fontWeight: 'bold'}}>{exactCount.toLocaleString()}</span>
                )}
            </Space>

            <Space style={{marginBottom: 8, display: 'flex', justifyContent: 'space-between'}}>
                <Space>
                    <Button
                        icon={<VerticalAlignTopOutlined />}
                        onClick={goStart}
                        disabled={loading}
                    >
                        {t('Start')}
                    </Button>
                    <Button
                        icon={<LeftOutlined />}
                        onClick={goPrev}
                        disabled={loading || !hasPrev}
                    >
                        {t('Prev')}
                    </Button>
                    <span style={{minWidth: 70, textAlign: 'center', display: 'inline-block'}}>
                        {t('Page')}: {pageIndex}
                    </span>
                    <Button
                        onClick={goNext}
                        disabled={loading || !hasNext}
                    >
                        {t('Next')} <RightOutlined />
                    </Button>
                </Space>
                <Space>
                    <Select
                        style={{width: 170}}
                        value={ascending}
                        onChange={changeOrder}
                        options={[
                            {value: false, label: t('Newest first')},
                            {value: true, label: t('Oldest first')},
                        ]}
                    />
                    <Select
                        style={{width: 110}}
                        value={pageSize}
                        onChange={setPageSize}
                        options={[50, 100, 200, 500].map(v => ({value: v, label: `${v} / ${t('page')}`}))}
                    />
                </Space>
            </Space>

            <Spin spinning={loading}>
                <div className="app-log-viewer">
                    {items.length === 0 && (
                        <div className="app-log-empty">
                            <Empty description={t('No logs found')} />
                        </div>
                    )}
                    {items.map(item => {
                        const meta = LEVELS[item.level] ?? {code: item.level?.substring(0, 3)?.toUpperCase(), color: '#000'};
                        const expanded = expandedIds.has(item.id);
                        return (
                            <div key={item.id}>
                                <div
                                    className={`app-log-row${expanded ? ' expanded' : ''}`}
                                    onClick={() => toggleExpanded(item.id)}
                                >
                                    <span className="app-log-time">{formatTime(item.timestamp)}</span>
                                    <span className="app-log-level" style={{color: meta.color}}>{meta.code}</span>
                                    <span
                                        className="app-log-user app-log-clickable"
                                        title={`${item.actorType ?? ''} ${item.actorName ?? item.userName ?? item.appClientName ?? ''}\n${t('Filter by this actor')}`}
                                        onClick={e => {
                                            e.stopPropagation();
                                            const name = item.actorName ?? item.userName ?? item.appClientName;
                                            if (name) filterByActor(name);
                                            else if (item.actorType) filterByActorType(item.actorType);
                                        }}
                                    >
                                        {item.actorType && (
                                            <span
                                                className="app-log-actor"
                                                style={{color: (ACTOR_TYPES[item.actorType]?.color) ?? '#8c8c8c'}}
                                            >
                                                {ACTOR_TYPES[item.actorType]?.code ?? item.actorType}
                                            </span>
                                        )}
                                        {item.actorName ?? item.userName ?? item.appClientName ?? '-'}
                                    </span>
                                    <span
                                        className="app-log-source app-log-clickable"
                                        title={`${item.sourceContext ?? ''}\n${t('Filter by this source')}`}
                                        onClick={e => { e.stopPropagation(); if (item.sourceContext) filterBySource(item.sourceContext); }}
                                    >
                                        {shortSource(item.sourceContext)}
                                    </span>
                                    <span
                                        className="app-log-req app-log-clickable"
                                        title={item.requestId ? `${item.requestId}\n${t('Trace this request')}` : ''}
                                        onClick={e => { e.stopPropagation(); if (item.requestId) filterByRequestId(item.requestId); }}
                                    >
                                        {item.requestId ? item.requestId.substring(0, 8) : ''}
                                    </span>
                                    <span className="app-log-message" title={item.message}>
                                        {item.channel && (
                                            <span
                                                className="app-log-channel app-log-clickable"
                                                title={t('Filter by this channel')}
                                                onClick={e => { e.stopPropagation(); filterByChannel(item.channel); }}
                                            >
                                                {item.channel}
                                            </span>
                                        )}
                                        {item.message}
                                    </span>
                                </div>
                                {expanded && (
                                    <div className="app-log-detail" onClick={e => e.stopPropagation()}>
                                        <div>
                                            <span className="app-log-detail-label">{t('Source')}:</span>
                                            {item.sourceContext ?? '-'}
                                            {item.channel && (
                                                <>
                                                    <span className="app-log-detail-label" style={{marginLeft: 16}}>{t('Channel')}:</span>
                                                    <span className="app-log-channel">{item.channel}</span>
                                                </>
                                            )}
                                            <span className="app-log-detail-label" style={{marginLeft: 16}}>{t('Actor')}:</span>
                                            {item.actorType && (
                                                <span
                                                    className="app-log-actor"
                                                    style={{color: (ACTOR_TYPES[item.actorType]?.color) ?? '#8c8c8c'}}
                                                >
                                                    {item.actorType}
                                                </span>
                                            )}
                                            {item.actorName ?? item.userName ?? '-'}
                                            {item.actorId ? ` (${item.actorId})` : ''}
                                            {item.ip && (
                                                <>
                                                    <span className="app-log-detail-label" style={{marginLeft: 16}}>{t('IP')}:</span>
                                                    <span
                                                        className="app-log-clickable"
                                                        style={{color: '#ad6800'}}
                                                        title={t('Filter by this IP')}
                                                        onClick={() => filterByIp(item.ip)}
                                                    >
                                                        {item.ip}
                                                    </span>
                                                </>
                                            )}
                                            {item.clientId && (
                                                <>
                                                    <span className="app-log-detail-label" style={{marginLeft: 16}}>{t('Device')}:</span>
                                                    <span
                                                        className="app-log-clickable"
                                                        style={{color: '#08979c'}}
                                                        title={t('Filter by this device')}
                                                        onClick={() => filterByClientId(item.clientId)}
                                                    >
                                                        {item.clientId}
                                                    </span>
                                                </>
                                            )}
                                            {item.requestId && (
                                                <>
                                                    <span className="app-log-detail-label" style={{marginLeft: 16}}>{t('Request Id')}:</span>
                                                    <span
                                                        className="app-log-clickable"
                                                        style={{color: '#1890ff'}}
                                                        title={t('Trace this request')}
                                                        onClick={() => filterByRequestId(item.requestId)}
                                                    >
                                                        {item.requestId}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        <div style={{marginTop: 4}}>
                                            <span className="app-log-detail-label">{t('Message')}:</span>
                                        </div>
                                        <pre>{item.message}</pre>
                                        {item.propertiesJson && (
                                            <>
                                                <span className="app-log-detail-label">{t('Properties')}:</span>
                                                <pre>{prettyJson(item.propertiesJson)}</pre>
                                            </>
                                        )}
                                        {item.exception && (
                                            <>
                                                <span className="app-log-detail-label" style={{color: '#f5222d'}}>{t('Exception')}:</span>
                                                <pre style={{color: '#a8071a'}}>{item.exception}</pre>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Spin>
        </>
    );
}
